import { useFieldSchema } from "$routes/_lib/validation/use-field-schema";
import type { OrderedJSONSchema } from "@elephant-healthcare/yupify";
import type { JSONSchema7TypeName } from "json-schema";
import React, { useMemo } from "react";
import type { AnySchema } from "yup";
import type { SchemaDescription } from "yup/lib/schema";
import { DynamicFieldsContext } from "./dynamic-form-fields-context";
import LazyField from "./lazy-field";

interface RadioOption {
  value: string;
  label: string;
}

interface DynamicFieldProps {
  name: string;
  fieldDef: OrderedJSONSchema;
  useFormValues: () => Record<string, unknown>;
  disabled?: boolean;
}

interface TheFieldProps
  extends Omit<DynamicFieldProps, "useFormValues" | "disabled"> {
  disabled: boolean;
  describedFieldSchema: SchemaDescription;
}

const TheField = ({
  name,
  fieldDef,
  describedFieldSchema: maybeDescribedFieldSchema,
  disabled,
  ...rest
}: TheFieldProps): React.ReactElement => {
  const { fieldMap } = React.useContext(DynamicFieldsContext);
  const resolvedMeta = (maybeDescribedFieldSchema?.meta ??
    fieldDef.meta ??
    {}) as JsonSchemaFieldMeta;
  if (resolvedMeta?.present === false) return null;

  const type = fieldDef.type as JSONSchema7TypeName;

  const describedFieldSchema = maybeDescribedFieldSchema ?? {
    meta: resolvedMeta,
    label: fieldDef.title,
    type,
    oneOf: fieldDef.enum ?? [],
    notOneOf: [],
    tests: [],
  };

  const isRequired =
    describedFieldSchema?.tests?.some(
      ({ name: testName }) => testName === "required",
    ) ?? false;

  if (resolvedMeta?.component) {
    return (
      <LazyField
        component={resolvedMeta.component}
        describedFieldSchema={describedFieldSchema}
        fieldDef={fieldDef}
        required={isRequired}
        name={name}
        type={type}
        disabled={disabled}
      />
    );
  }

  // Pass `options` through to the DynamicInput, so it can render an appropriate radio component
  let options: RadioOption[];
  if (describedFieldSchema.oneOf.length > 0) {
    options = describedFieldSchema.oneOf.map(
      (option: string, index: number) => ({
        value: option,
        label: resolvedMeta.enumTitles[index],
      }),
    );
  }

  // Select component based on type and format
  // For string type with date format, use date component if available
  let fieldKey: string = type;
  if (type === "string" && fieldDef.format === "date" && fieldMap.date) {
    fieldKey = "date";
  }

  const Field = fieldMap[fieldKey];
  if (!Field) return null;

  const fieldProps = {
    name,
    fieldDef,
    options,
    label: describedFieldSchema.label,
    required: isRequired,
    disabled,
  } as Record<string, unknown>;

  return <Field {...rest} {...fieldProps} />;
};

const TheFieldMemoised = React.memo(TheField);

interface DynamicFieldChildProps extends Omit<DynamicFieldProps, "disabled"> {
  disabled: boolean;
  fieldSchema: AnySchema;
}

interface JsonSchemaFieldMeta {
  present?: boolean;
  component?: string;
  enumTitles?: string[];
}

// "resolved" fields have a `.when()` clause;
// their validation rules depend on the form state,
// therefore we need to `.resolve()` its schema with the form state
export const DynamicResolvedField = ({
  name,
  fieldSchema,
  useFormValues,
  disabled,
  ...props
}: DynamicFieldChildProps): React.ReactElement => {
  const values = useFormValues();

  const describedFieldSchema = useMemo(
    () => fieldSchema?.resolve({ parent: values }).describe(),
    [fieldSchema, values],
  );

  return (
    <TheFieldMemoised
      name={name}
      describedFieldSchema={describedFieldSchema}
      disabled={disabled}
      {...props}
    />
  );
};

// "non-resolved" fields don't depend on the form state
export const DynamicNonResolvedField = ({
  name,
  fieldSchema,
  useFormValues: _,
  disabled,
  ...props
}: DynamicFieldChildProps): React.ReactElement => {
  const describedFieldSchema = fieldSchema?.describe();
  return (
    <TheFieldMemoised
      name={name}
      describedFieldSchema={describedFieldSchema}
      disabled={disabled}
      {...props}
    />
  );
};

export const DynamicField = ({
  name,
  disabled,
  ...rest
}: DynamicFieldProps): React.ReactElement => {
  const fieldSchema = useFieldSchema(name);
  const DynamicFieldComponent =
    fieldSchema?.deps?.length > 0
      ? DynamicResolvedField
      : DynamicNonResolvedField;
  return (
    <DynamicFieldComponent
      {...rest}
      name={name}
      disabled={disabled}
      fieldSchema={fieldSchema}
    />
  );
};

DynamicField.defaultProps = {
  disabled: false,
};

export default DynamicField;
